import type { WindowGeneratorPayload } from '../../../services/windowGeneratorTemplates.service';

export type V2PreviewUrls = {
  designExternalUrl: string;
  designInternalUrl: string;
  presentationExternalUrl: string;
  presentationInternalUrl: string;
  baySchematicUrl: string;
};

export function attachV2LitePreviewUrls<T extends Record<string, unknown>>(
  windowStyle: T,
  urls: V2PreviewUrls
): T {
  return {
    ...windowStyle,
    preview_path_external: urls.presentationExternalUrl,
    preview_path_internal: urls.presentationInternalUrl,
    email_path_external: urls.presentationExternalUrl,
    email_path_internal: urls.presentationInternalUrl,
    preview_path_design_external: urls.designExternalUrl,
    preview_path_design_internal: urls.designInternalUrl,
    preview_path_presentation_external: urls.presentationExternalUrl,
    preview_path_presentation_internal: urls.presentationInternalUrl,
    preview_path_bay_schematic: urls.baySchematicUrl,
    preview_path_plan_external: urls.baySchematicUrl,
    email_path_design_external: urls.designExternalUrl,
    email_path_design_internal: urls.designInternalUrl,
    email_path_presentation_external: urls.presentationExternalUrl,
    email_path_presentation_internal: urls.presentationInternalUrl,
    email_path_bay_schematic: urls.baySchematicUrl,
  };
}

/** Single payload resolver for v2_lite modal Preview, Configure, Save, and PNG generation. */
export function resolveV2LiteModalPayload(
  workingPayload: WindowGeneratorPayload | null | undefined,
  v2PayloadToUse: WindowGeneratorPayload | null | undefined,
  libraryPayload: WindowGeneratorPayload | null | undefined
): WindowGeneratorPayload | null {
  return (workingPayload || v2PayloadToUse || libraryPayload || null) as WindowGeneratorPayload | null;
}

export function resolveCanonicalTemplatePayload(
  meta: { window_style?: Record<string, unknown> },
  libraryTemplates: Array<{ id?: unknown; payload?: WindowGeneratorPayload }>
): WindowGeneratorPayload | null {
  const wsAny = (meta.window_style || {}) as Record<string, unknown>;
  const styleId = wsAny.id;
  const fromLibrary = libraryTemplates.find((t) => String(t?.id) === String(styleId));
  const isWindowGeneratorStyle =
    String(wsAny.source || '').trim().toLowerCase() === 'window_generator' || !!fromLibrary;

  if (!isWindowGeneratorStyle) return null;

  const payloadCandidate =
    (wsAny.templatePayload as WindowGeneratorPayload | undefined) ||
    (wsAny.template_payload as WindowGeneratorPayload | undefined) ||
    (wsAny.payload as WindowGeneratorPayload | undefined) ||
    null;

  const hasPayload = !!(
    payloadCandidate &&
    (payloadCandidate as { root?: unknown }).root &&
    payloadCandidate.w != null &&
    payloadCandidate.h != null
  );

  if (hasPayload) return payloadCandidate;

  const fallbackPayload = fromLibrary?.payload;
  if (
    fallbackPayload &&
    (fallbackPayload as { root?: unknown }).root &&
    fallbackPayload.w != null &&
    fallbackPayload.h != null
  ) {
    return fallbackPayload;
  }

  return null;
}

export function qualifiesForV2LiteWgBulk(
  meta: { designer_version?: string; window_style?: Record<string, unknown> },
  libraryTemplates: Array<{ id?: unknown; payload?: WindowGeneratorPayload }>
): boolean {
  if (String(meta?.designer_version || '').trim() !== 'v2_lite') return false;
  return !!resolveCanonicalTemplatePayload(meta, libraryTemplates);
}

export function mergeBulkResultsIntoLines(
  base: import('../types/documents.types').DocumentLineInput[],
  results: Map<number, import('../types/documents.types').DocumentLineInput>
): import('../types/documents.types').DocumentLineInput[] {
  const next = [...base];
  for (const [index, updatedLine] of results) {
    if (index < 0 || index >= next.length) continue;
    next[index] = updatedLine;
  }
  return next.map((line, i) => ({ ...line, sortOrder: i }));
}
